// ─── Shared template repository client ──────────────────────────────────────
//
// One real, tenant-scoped, database-backed template library (server.js's
// /api/templates routes + db.js's Template model) — the actual source of
// truth now, reachable from its own "Templates" page in the main app
// sidebar and consumed identically from Mail Merge, Sequences, and
// Workspace. Those three all run as separate popup windows/React trees
// (Workspace and Mail Merge as actual browser popups; Sequences inside the
// main shell) with no shared React state, so this module — not a context
// provider — is what "the same templates everywhere" actually means here.
//
// localStorage stays in the picture, but demoted to what it's actually good
// at: a same-origin, cross-*window* live cache. The native `storage` event
// fires in one popup the instant another same-origin window writes this
// key, which is what lets Workspace's template list stay live while Mail
// Merge is open and saving templates alongside it, with no polling. Every
// fetch from the server below also writes through to localStorage, so that
// cross-window notification keeps working exactly as it did when
// localStorage was the only store.
const KEY = "sdr_email_templates";

// "based on the use choice" from the product ask — a template applies to
// either a first-touch/fresh outreach email or a follow-up, or is generic
// enough for either. Kept as a small fixed set (matches db.js's schema
// enum) rather than free-form tags, so every surface's filter UI can just
// be three buttons instead of a tag picker.
export const TEMPLATE_CATEGORIES = [
  { id: "fresh",    label: "Fresh Email", hint: "First-touch / new outreach" },
  { id: "followup", label: "Follow-up",   hint: "Replies and nudges on an existing thread" },
  { id: "general",  label: "General",     hint: "Works for either" },
];

function authHeaders(json) {
  const token = localStorage.getItem("sdr_token") || "";
  return json
    ? { "Content-Type": "application/json", Authorization: "Bearer " + token }
    : { Authorization: "Bearer " + token };
}

// ─── Cross-window local cache (unchanged shape/behavior from before) ───────
export function loadTemplates(seedIfEmpty){
  try{
    const raw = localStorage.getItem(KEY);
    if(raw){
      const parsed = JSON.parse(raw);
      if(Array.isArray(parsed)) return parsed;
    }
  }catch(e){}
  const seeded = seedIfEmpty || [];
  if(seeded.length) saveTemplates(seeded);
  return seeded;
}

export function saveTemplates(templates){
  try{ localStorage.setItem(KEY, JSON.stringify(templates)); }catch(e){}
}

export const TEMPLATES_STORAGE_KEY = KEY;

// ─── Server-backed repository (source of truth) ────────────────────────────
// Every function below is best-effort: on any network/auth failure it falls
// back to whatever's already in the local cache rather than throwing, so a
// surface that calls this during a flaky connection still shows the last
// known list instead of an error state.

// Fetches the full repository (optionally filtered by category), and writes
// it through to the localStorage cache so every other open window updates
// live via the `storage` event, same as a local save always has.
export async function fetchTemplateRepo(category){
  try{
    const qs = category ? ("?category=" + encodeURIComponent(category)) : "";
    const res = await fetch("/api/templates" + qs, { headers: authHeaders(false) });
    if(!res.ok) throw new Error("HTTP " + res.status);
    const data = await res.json();
    const list = Array.isArray(data) ? data : [];
    if(!category) saveTemplates(list); // only overwrite the full local cache from an unfiltered fetch
    return list;
  }catch(e){
    return loadTemplates([]);
  }
}

export async function createTemplateRepo({ name, category, subject, body }){
  const res = await fetch("/api/templates", { method: "POST", headers: authHeaders(true), body: JSON.stringify({ name, category, subject, body }) });
  const data = await res.json().catch(() => ({}));
  if(!res.ok) throw new Error(data.error || "Could not save template.");
  return data;
}

export async function updateTemplateRepo(id, patch){
  const res = await fetch(`/api/templates/${id}`, { method: "PUT", headers: authHeaders(true), body: JSON.stringify(patch) });
  const data = await res.json().catch(() => ({}));
  if(!res.ok) throw new Error(data.error || "Could not update template.");
  return data;
}

export async function deleteTemplateRepo(id){
  const res = await fetch(`/api/templates/${id}`, { method: "DELETE", headers: authHeaders(false) });
  const data = await res.json().catch(() => ({}));
  if(!res.ok) throw new Error(data.error || "Could not delete template.");
  return data;
}

// Fire-and-forget usage counter — a surface calls this the instant it
// actually inserts a template into a compose/step. Never throws; a failed
// usage ping must never block or undo the insert that already happened.
export function markTemplateUsed(id){
  if(!id) return;
  fetch(`/api/templates/${id}/use`, { method: "POST", headers: authHeaders(false) }).catch(() => {});
}
